import type {
  ScheduledJobAdvancedInput,
  ScheduledJobRetryConfig,
} from "@/features/scheduled-jobs/api/scheduled-jobs.api";
import type { ScheduledJobAdvancedFormState } from "../components/ScheduledJobAdvancedFields";
import { hasEventTypeAttribute, parseAttributesJson, parseBodyJson } from "./scheduled-job-preview";

/** Matches API `ATTEMPT_DEADLINE_*` bounds (Cloud Scheduler HTTP Console range). */
const ATTEMPT_DEADLINE_MIN_SECONDS = 15;
const ATTEMPT_DEADLINE_MAX_SECONDS = 1800;

export type AdvancedPayloadBuildResult =
  | { ok: true; value: ScheduledJobAdvancedInput | undefined }
  | {
      ok: false;
      error:
        | "attributes"
        | "body"
        | "retry"
        | "attemptDeadline"
        | "incomplete"
        | "eventType"
        | "topic";
    };

/**
 * Build the wire `advanced` payload from the Advanced form.
 * Returns `undefined` when Advanced is closed / untouched (no override).
 */
export function buildAdvancedPayload(options: {
  open: boolean;
  form: ScheduledJobAdvancedFormState;
  /** When true, include the Pub/Sub trio when any pubsub field is non-empty. */
  includePubsub: boolean;
}): AdvancedPayloadBuildResult {
  if (!options.open) return { ok: true, value: undefined };

  const retry = parseRetryConfig(options.form);
  if (!retry.ok) return { ok: false, error: "retry" };

  const deadline = parseAttemptDeadline(options.form.attemptDeadlineSeconds);
  if (!deadline.ok) return { ok: false, error: "attemptDeadline" };

  let topicName: string | undefined;
  let attributes: Record<string, string> | undefined;
  let bodyJson: unknown | undefined;

  if (options.includePubsub) {
    const topic = options.form.topicName.trim();
    const attributesParsed = parseAttributesJson(options.form.attributesJson);
    if (attributesParsed === null) return { ok: false, error: "attributes" };
    const bodyParsed = parseBodyJson(options.form.bodyJson);
    if (!bodyParsed.ok) return { ok: false, error: "body" };

    const hasAnyPubsub =
      topic.length > 0 ||
      Object.keys(attributesParsed).length > 0 ||
      options.form.bodyJson.trim().length > 0;

    if (hasAnyPubsub) {
      if (!topic || !bodyParsed.ok) return { ok: false, error: "incomplete" };
      if (!topic.startsWith("projects/")) return { ok: false, error: "topic" };
      if (!hasEventTypeAttribute(attributesParsed)) return { ok: false, error: "eventType" };
      topicName = topic;
      attributes = attributesParsed;
      bodyJson = bodyParsed.value;
    }
  }

  if (
    topicName === undefined &&
    attributes === undefined &&
    bodyJson === undefined &&
    retry.value === undefined &&
    deadline.value === undefined
  ) {
    return { ok: true, value: undefined };
  }

  return {
    ok: true,
    value: {
      ...(topicName !== undefined
        ? { topicName, attributes: attributes!, bodyJson }
        : {}),
      ...(retry.value !== undefined ? { retryConfig: retry.value } : {}),
      ...(deadline.value !== undefined ? { attemptDeadlineSeconds: deadline.value } : {}),
    },
  };
}

/** True when topic / attributes / body differ from the curated Advanced seed. */
export function isAdvancedPubsubDirty(
  form: ScheduledJobAdvancedFormState,
  seed: ScheduledJobAdvancedFormState,
): boolean {
  if (form.topicName.trim() !== seed.topicName.trim()) return true;

  const formAttrs = parseAttributesJson(form.attributesJson);
  const seedAttrs = parseAttributesJson(seed.attributesJson);
  if (formAttrs === null || seedAttrs === null) return true;
  if (!stringRecordsEqual(formAttrs, seedAttrs)) return true;

  const formBody = parseBodyJson(form.bodyJson);
  const seedBody = parseBodyJson(seed.bodyJson);
  if (!formBody.ok || !seedBody.ok) return true;
  return JSON.stringify(formBody.value) !== JSON.stringify(seedBody.value);
}

/** Field-level equality for drawer dirty tracking against the loaded job snapshot. */
export function advancedFormsEqual(
  a: ScheduledJobAdvancedFormState,
  b: ScheduledJobAdvancedFormState,
): boolean {
  return (
    a.topicName === b.topicName &&
    a.attributesJson === b.attributesJson &&
    a.bodyJson === b.bodyJson &&
    a.retryCount === b.retryCount &&
    a.maxRetryDurationSeconds === b.maxRetryDurationSeconds &&
    a.minBackoffDurationSeconds === b.minBackoffDurationSeconds &&
    a.maxBackoffDurationSeconds === b.maxBackoffDurationSeconds &&
    a.maxDoublings === b.maxDoublings &&
    a.attemptDeadlineSeconds === b.attemptDeadlineSeconds
  );
}

function stringRecordsEqual(
  a: Record<string, string>,
  b: Record<string, string>,
): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

function parseRetryConfig(
  form: ScheduledJobAdvancedFormState,
): { ok: true; value: ScheduledJobRetryConfig | undefined } | { ok: false } {
  const fields: Array<[keyof ScheduledJobRetryConfig, string]> = [
    ["retryCount", form.retryCount],
    ["maxRetryDurationSeconds", form.maxRetryDurationSeconds],
    ["minBackoffDurationSeconds", form.minBackoffDurationSeconds],
    ["maxBackoffDurationSeconds", form.maxBackoffDurationSeconds],
    ["maxDoublings", form.maxDoublings],
  ];

  const out: ScheduledJobRetryConfig = {};
  let any = false;
  for (const [key, raw] of fields) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const num = Number(trimmed);
    if (!Number.isInteger(num) || num < 0) return { ok: false };
    out[key] = num;
    any = true;
  }
  return { ok: true, value: any ? out : undefined };
}

function parseAttemptDeadline(
  raw: string,
): { ok: true; value: number | undefined } | { ok: false } {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: true, value: undefined };
  const num = Number(trimmed);
  if (
    !Number.isInteger(num) ||
    num < ATTEMPT_DEADLINE_MIN_SECONDS ||
    num > ATTEMPT_DEADLINE_MAX_SECONDS
  ) {
    return { ok: false };
  }
  return { ok: true, value: num };
}
